import { useCallback, useReducer } from 'react';
/*
  This hook manages form state and validity. It is meant to be used with the "smart"
  input components that pass value and { isValid, name } to their onChange handlers.

  The hook returns three values:
  - form (a simple object with the current form values)
  - updateForm (a function to update an individual field)
  - isValid (a boolean that indicates if _all_ the form fields are valid)

  When you see a component like this:
  <Inputs.FirstName onChange={updateForm} value={form.firstName}/>

  The updateForm function is being called like this:
  updateForm('Bob', { isValid: true, name: 'firstName' })
*/

type Field<Value = string> = { isValid: boolean; value: Value; optional: boolean };
type State<Form> = {
  [key in keyof Form]: Field<Form[key]>;
};

const reducer = <Form>(
  currentFields: State<Form>,
  updatedField: {
    [key in keyof Form]: Field<Form[key]>;
  },
) => ({
  ...currentFields,
  ...updatedField,
});

// parse the initial values passed to the hook
const prepareInput = <Form extends object>(initialValues: Form) =>
  Object.keys(initialValues).reduce(
    (fields, fieldName) => ({
      ...fields,
      [fieldName]: { isValid: false, value: initialValues[fieldName], optional: false },
    }),
    {} as State<Form>,
  );

// parse the reducer state back into a nice format
const prepareOutput = <State extends object>(reducerState: State) =>
  Object.keys(reducerState).reduce((fields, fieldName) => {
    const { value } = reducerState[fieldName];
    return {
      ...fields,
      [fieldName]: value,
    };
  }, {});

/**
 * Returns whether hte input is considered an 'empty' value for optional inputs.
 */
export const isEmptyValue = (value: string): boolean => {
  return value === '';
};

/**
 * Params passed back to the useForm hook when a field in a form is updated.
 * Contains data about the current state of an individual input in the form.
 */
type FormUpdateParams<Form> = {
  name: keyof Form;
  isValid: boolean;
  optional?: boolean;
};

export const useForm = <Form extends Record<string, any>>(initialValues: Form) => {
  const [state, dispatch] = useReducer(reducer, prepareInput(initialValues));
  const updateForm = useCallback((value, { name, isValid, optional }: FormUpdateParams<Form>) => {
    dispatch({ [name]: { isValid, value, optional: !!optional } });
  }, []);
  // To reset the form, we just set all fields to their initial values
  const resetForm = useCallback(() => {
    dispatch(prepareInput(initialValues));
  }, [initialValues]);
  const isValid = Object.values(state).every((field: Field) => field.isValid);
  const form = prepareOutput(state);
  return [form, updateForm, isValid, resetForm] as [
    Form,
    typeof updateForm,
    boolean,
    typeof resetForm,
  ];
};
